import React, { useEffect, useState, useRef } from "react";

/**
 * SPONSOR / HERO CAROUSEL
 * - Admin SettingsPanel'den eklenen sponsor_banners döngüsel oynatılır
 * - banner_rotation_seconds admin tarafından belirlenir (default 6)
 * - start_at/end_at ile zamanlanmış bannerlar: pencereye düşmeyenler atlanır
 * - Hiç banner yoksa null döner → Home.jsx fallback hero gösterir
 * - Tıklanabilir link opsiyonel
 */
export default function HeroCarousel({ banners = [], rotationSeconds = 6 }) {
  const now = Date.now();
  const inWindow = (b) => {
    const s = b.start_at ? new Date(b.start_at).getTime() : null;
    const e = b.end_at ? new Date(b.end_at).getTime() : null;
    if (s && now < s) return false;
    if (e && now > e) return false;
    return true;
  };
  const enabled = (banners || []).filter(
    (b) => b?.enabled !== false && b?.image_url && inWindow(b)
  );
  const sorted = [...enabled].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  const touchX = useRef(null);

  // Auto-rotate
  useEffect(() => {
    if (sorted.length <= 1) return;
    const ms = Math.max(2, Math.min(60, rotationSeconds || 6)) * 1000;
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % sorted.length);
    }, ms);
    return () => clearInterval(timerRef.current);
  }, [sorted.length, rotationSeconds]);

  // Reset to first when banner list changes
  useEffect(() => {
    setIdx(0);
  }, [sorted.length]);

  if (sorted.length === 0) return null;

  const go = (nextIdx) => {
    setIdx(((nextIdx % sorted.length) + sorted.length) % sorted.length);
    // reset timer so user action resets the countdown
    if (timerRef.current) {
      clearInterval(timerRef.current);
      const ms = Math.max(2, Math.min(60, rotationSeconds || 6)) * 1000;
      timerRef.current = setInterval(() => {
        setIdx((i) => (i + 1) % sorted.length);
      }, ms);
    }
  };

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? idx + 1 : idx - 1);
    touchX.current = null;
  };

  const current = sorted[idx];

  const fit = current.fit === "contain" ? "contain" : "cover";
  const focalX = typeof current.focal_x === "number" ? current.focal_x : 50;
  const focalY = typeof current.focal_y === "number" ? current.focal_y : 50;
  const zoom = Math.max(1, Math.min(3, Number(current.zoom) || 1));
  const bg = current.bg_color || "#1B2A22";

  const Img = (
    <img
      src={current.image_url}
      alt={current.title || "Sponsor"}
      className="w-full h-full transition-opacity duration-700"
      style={{
        objectFit: fit,
        objectPosition: `${focalX}% ${focalY}%`,
        transform: fit === "cover" && zoom > 1 ? `scale(${zoom})` : undefined,
        transformOrigin: `${focalX}% ${focalY}%`,
        background: bg,
      }}
      loading="eager"
      decoding="async"
    />
  );

  return (
    <section
      data-testid="hero-carousel"
      className="relative overflow-hidden border-b border-[#E8E2D5] bg-[#1B2A22]"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Image slot — responsive 16:9 on mobile, more banner-like on desktop */}
      <div
        className="relative w-full aspect-[16/9] sm:aspect-[21/9] lg:aspect-[32/9] max-h-[520px] overflow-hidden"
        style={{ background: current.bg_color || "#1B2A22" }}
      >
        {current.link ? (
          <a
            href={current.link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`hero-banner-link-${idx}`}
            className="block w-full h-full"
          >
            {Img}
          </a>
        ) : (
          <div data-testid={`hero-banner-slide-${idx}`} className="w-full h-full">{Img}</div>
        )}

        {/* Subtle gradient overlay for readability of label */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent" />

        {/* Sponsor badge */}
        <div className="absolute top-3 left-3 bg-black/55 backdrop-blur text-white text-[10px] uppercase tracking-wider font-heading font-bold px-2.5 py-1 rounded-full">
          {current.title || "SPONSOR"}
        </div>

        {/* Prev/Next (desktop only, if 2+ banners) */}
        {sorted.length > 1 && (
          <>
            <button
              data-testid="hero-banner-prev"
              onClick={() => go(idx - 1)}
              aria-label="Önceki"
              className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 text-white items-center justify-center backdrop-blur transition"
            >
              ‹
            </button>
            <button
              data-testid="hero-banner-next"
              onClick={() => go(idx + 1)}
              aria-label="Sonraki"
              className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 text-white items-center justify-center backdrop-blur transition"
            >
              ›
            </button>
          </>
        )}

        {/* Dots */}
        {sorted.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="hero-banner-dots">
            {sorted.map((_, i) => (
              <button
                key={i}
                onClick={() => go(i)}
                aria-label={`Banner ${i + 1}`}
                data-testid={`hero-banner-dot-${i}`}
                className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-2 bg-white/50 hover:bg-white/80"}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
